/* An open product's heading (designs/products-pdp.md PP18 and the UI
   section, "Record heading" + "Change phase form"): the phase chip in one of
   its honest states, the declaration stamp, and the record's one action
   surface — Change phase… as the primary, the ⋯ overflow holding Withdraw
   declaration…, an intake Admin's Edit product… and Retire / Bring
   back (products-manage.md PM3, reshaped by teams-and-products.md TP3), and
   Close. The change-phase and rename forms
   replace the action row in place; the unchosen arm is disabled so a save
   carries only the chosen one; the shared rule refuses sooner with the same
   words the server would use. */
import { useEffect, useRef, useState, type FormEvent, type JSX, type KeyboardEvent } from "react";
import { validateProductEdit, type Product, type ProductEdit, type Vehicle } from "../../../shared/products";
import { ownsProducts, type CanonicalTeam } from "../../../shared/canonical-teams";
import { getTeams, postVehicle, putVehicle } from "../intake/api";
import { loadJson } from "./store";
import { PDP_PHASE_NAMES, PDP_PHASES, type PdpPhase } from "../../../shared/pdp";
import {
  declarationReason,
  utcToday,
  type CurrentDeclaration,
  type PhaseDeclarationInput,
} from "../../../shared/product-pdp";
import { useAppState } from "./store";
import { declarationStamp, gateAfter, pdpFor } from "../views/products/pdpText";
import { declarePhase, editProduct } from "../views/products.actions";

const MORE = "More actions";

const localToday = (): string => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
};

function Chip({ decl, retired }: { decl: CurrentDeclaration | null; retired: boolean }): JSX.Element {
  if (retired) return <span className="state retired">retired</span>;
  if (!decl) return <span className="state missing">not recorded</span>;
  if (decl.kind === "not-in-pdp") return <span className="state not-in-pdp">not in the PDP</span>;
  return <span className="state phase-chip">Phase {decl.phase}</span>;
}

export function ProductHeader({ product }: { product: Product }): JSX.Element {
  const { state, store } = useAppState();
  const pdp = pdpFor(state.productPdp, product.id);
  const decl = pdp.declaration;
  const retired = product.retired === true;
  const open = state.openMenu === MORE;
  /* the gate asIntakeAdmin enforces server-side, said the same way here
     (PM3/PM4): a non-Admin sees neither Rename… nor Retire */
  const intakeAdmin = state.me.roles.includes("admin");
  const changeRef = useRef<HTMLButtonElement>(null);
  const moreRef = useRef<HTMLButtonElement>(null);
  /* a refused withdrawal or retire is brought to the reader, like a refused save */
  const recordErrorRef = useRef<HTMLParagraphElement>(null);
  useEffect(() => {
    if (state.listError) recordErrorRef.current?.focus();
  }, [state.listError]);
  /* Cancel / save return focus to the button that opened the form: Change
     phase… for the phase form, the persistent ⋯ for Rename… (the menu item
     that opened it is gone with the menu) */
  const renaming = state.productFormOpen === "rename";
  const [returnTo, setReturnTo] = useState<"change" | "more" | null>(null);
  useEffect(() => {
    if (returnTo && !state.phaseFormOpen && !renaming) {
      (returnTo === "change" ? changeRef : moreRef).current?.focus();
      setReturnTo(null);
    }
  }, [returnTo, state.phaseFormOpen, renaming]);

  const closeForm = (): void => {
    setReturnTo("change");
    store.update({ phaseFormOpen: false });
  };
  const closeRename = (): void => {
    setReturnTo("more");
    store.update({ productFormOpen: null });
  };
  /* one product write at a time from this header (Copilot review of #133):
     while a retire or bring-back is out, Rename… and Retire wait, so an older
     answer can never land over a newer one */
  const [busy, setBusy] = useState(false);

  /* PM3: retire is a reversible flag, but it leaves the same overflow menu as
     Withdraw declaration…, so it asks the same way; Bring back does not */
  const setRetired = async (retired: boolean): Promise<void> => {
    if (
      retired &&
      !window.confirm(
        `Retire ${product.name}? It leaves the default table and its record becomes read-only; Bring back reverses it.`,
      )
    )
      return;
    if (busy) return;
    setBusy(true);
    store.update({ listError: null });
    const reason = await editProduct(store, product.id, { retired });
    setBusy(false);
    if (reason) store.update({ listError: reason });
  };

  /* a refused withdrawal is shown HERE, on the record (the table's listError
     line is not on this page); closeProduct() clears it */
  const withdraw = async (): Promise<void> => {
    if (!window.confirm(`Withdraw the PDP declaration for ${product.name}? Evidence stays in history.`)) return;
    store.update({ listError: null });
    const reason = await declarePhase(store, product.id, { kind: "withdrawn" });
    if (reason) store.update({ listError: reason });
  };

  /* Record gate review… (PP7): on the Phase area, while a phase stands and a
     gate follows it (none after Phase 6). The Admin gate is server-side; a
     non-Admin opens the sheet read-only (Screen 5). */
  const gateAhead = decl?.kind === "phase" ? gateAfter(state.pdp.gates, decl.phase) : undefined;
  const canReview = state.area === "Phase" && gateAhead !== undefined && !state.openCertificateId;

  const ops: { label: string; run: () => void; disabled?: boolean }[] = [
    ...(decl && !retired ? [{ label: "Withdraw declaration…", run: (): void => void withdraw() }] : []),
    ...(intakeAdmin
      ? [
          {
            label: "Edit product…",
            run: (): void => store.update({ productFormOpen: "rename", listError: null }),
            disabled: busy,
          },
          retired
            ? { label: "Bring back", run: (): void => void setRetired(false), disabled: busy }
            : { label: "Retire", run: (): void => void setRetired(true), disabled: busy },
        ]
      : []),
    { label: "Close", run: (): void => store.closeProduct() },
  ];

  const more = (
    <div className="nav-menu-wrap">
      <button
        ref={moreRef}
        className={"record-more" + (open ? " open" : "")}
        aria-label={MORE}
        aria-haspopup="menu"
        aria-controls="nav-menu-panel"
        aria-expanded={open}
        onClick={() => store.toggleMenuBar(MORE)}
      >
        ⋯
      </button>
      {open && (
        <div className="nav-menu-panel" id="nav-menu-panel" aria-label={MORE}>
          {ops.map((o) => (
            <button
              key={o.label}
              className="nav-menu-item"
              disabled={o.disabled === true}
              onClick={() => {
                store.toggleMenuBar(MORE);
                o.run();
              }}
            >
              {o.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <>
      <div className="nav-stamp">
        <Chip decl={decl} retired={retired} />
        {decl && <span className="stamp">{declarationStamp(decl)}</span>}
        {decl?.kind === "phase" && decl.unconfirmed && <span className="state unconfirmed">unconfirmed</span>}
      </div>
      {state.listError && !state.phaseFormOpen && (
        <p className="form-error record-error" role="alert" tabIndex={-1} ref={recordErrorRef}>
          {state.listError}
        </p>
      )}
      {renaming ? (
        <SettingsForm product={product} onClose={closeRename} />
      ) : retired ? (
        <>
          <p className="slot-note">Retired — read-only.</p>
          {intakeAdmin && <div className="record-actions">{more}</div>}
        </>
      ) : state.phaseFormOpen ? (
        <PhaseForm product={product} current={decl} onClose={closeForm} />
      ) : (
        <div className="record-actions">
          <button
            id="change-phase"
            ref={changeRef}
            className="btn primary"
            onClick={() => store.update({ phaseFormOpen: true, openMenu: null, listError: null })}
          >
            Change phase…
          </button>
          {canReview && (
            <button
              id="record-review"
              className="btn ghost"
              aria-pressed={state.reviewDraft}
              onClick={() => store.update({ reviewDraft: true, reviewGateId: null, openMenu: null, listError: null })}
            >
              Record gate review…
            </button>
          )}
          {more}
        </div>
      )}
    </>
  );
}

/* Edit product… (teams-and-products.md TP3/TP4, option B), Admin: the
   product's own name, the team that owns it (a team may
   own several — moving a product moves its PDP and vehicles with it), its
   market, and its vehicles with their review flag. Each change is an
   appended revision. */
function SettingsForm({ product, onClose }: { product: Product; onClose: () => void }): JSX.Element {
  const { state, store } = useAppState();
  const unfiled = product.teamId == null;
  const [name, setName] = useState(product.name);
  const [market, setMarket] = useState(product.market ?? "");
  const [teamId, setTeamId] = useState(product.teamId ?? "");
  const [teams, setTeams] = useState<CanonicalTeam[]>([]);
  const [newVehicle, setNewVehicle] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [refusals, setRefusals] = useState(0);
  const [saving, setSaving] = useState(false);
  const firstRef = useRef<HTMLInputElement>(null);
  const errorRef = useRef<HTMLParagraphElement>(null);

  useEffect(() => firstRef.current?.focus(), []);
  /* the team list is read as the form opens, so the Team select shows the
     owning team's NAME at once; a failed read leaves the id showing */
  useEffect(() => {
    void getTeams()
      .then(setTeams)
      .catch(() => setTeams([]));
  }, []);
  useEffect(() => {
    if (refusals > 0) errorRef.current?.focus();
  }, [refusals]);

  const refuse = (reason: string): void => {
    setError(reason);
    setRefusals((n) => n + 1);
  };
  const vehicles = state.vehicles.filter((v) => v.productId === product.id);
  /* any live team with a type may own it — platform teams own platform
     products (option B: several each) */
  const owners = teams.filter(ownsProducts);

  const reloadVehicles = async (): Promise<void> => {
    const fresh = await loadJson<Vehicle[]>("/api/intake/vehicles").catch(() => state.vehicles);
    store.update({ vehicles: fresh });
  };
  const vehicleAct = async (act: () => Promise<unknown>): Promise<void> => {
    setError(null);
    try {
      await act();
      await reloadVehicles();
    } catch (e) {
      refuse(e instanceof Error ? e.message : String(e));
    }
  };

  const save = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    if (saving) return;
    const trimmedMarket = market.trim();
    const edit: ProductEdit = {
      ...(name.trim() !== product.name ? { name: name.trim() } : {}),
      ...(trimmedMarket !== (product.market ?? "") ? { market: trimmedMarket === "" ? null : trimmedMarket } : {}),
      ...(teamId !== (product.teamId ?? "") ? { teamId: teamId === "" ? null : teamId } : {}),
    };
    if (Object.keys(edit).length === 0) return onClose();
    const sooner = validateProductEdit(
      edit,
      state.products,
      product.id,
      owners.map((t) => t.id).concat(product.teamId ?? []),
    );
    if (sooner) return refuse(sooner);
    setSaving(true);
    const refused = await editProduct(store, product.id, edit);
    setSaving(false);
    if (refused) return refuse(refused);
    onClose();
  };

  const onKey = (e: KeyboardEvent): void => {
    if (e.key === "Escape") onClose();
  };

  return (
    <form id="product-settings-form" className="card-form" onSubmit={(e) => void save(e)} onKeyDown={onKey}>
      {unfiled && (
        <p className="slot-note">No team owns this product yet — choose its team below.</p>
      )}
      <label className="field">
        Name{" "}
        <input
          id="rename-product-name"
          type="text"
          ref={firstRef}
          maxLength={200}
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
      </label>
      <label
        className="field"
        title="The team that owns this product — platform teams own platform products. A team may own several products, each its own PDP; moving one moves its PDP and vehicles with it."
      >
        Team{" "}
        <select id="product-team" value={teamId} onChange={(e) => setTeamId(e.target.value)}>
          <option value="">— not filed under a team —</option>
          {/* the current team shows (as its id) until the list is read */}
          {product.teamId != null && !owners.some((t) => t.id === product.teamId) && (
            <option value={product.teamId}>{product.teamId}</option>
          )}
          {owners.map((t) => (
            <option key={t.id} value={t.id}>
              {t.name}
            </option>
          ))}
        </select>
      </label>
      <label className="field" title="A grouping across product teams (Construction, Ag, Landscaping…). The exec rollup groups by it.">
        Market{" "}
        <input
          id="product-market"
          type="text"
          maxLength={100}
          value={market}
          placeholder="not recorded"
          onChange={(e) => setMarket(e.target.value)}
        />
      </label>
      <fieldset className="field product-vehicles">
        <legend title="A product can have several vehicles. A vehicle a requester added waits for your review.">
          Vehicles
        </legend>
        {vehicles.length === 0 && <p className="slot-note">None recorded yet.</p>}
        {vehicles.map((v) => (
          <div key={v.id} className="vehicle-row" data-vehicle-row={v.id}>
            <input
              key={v.name}
              className="cell-edit"
              defaultValue={v.name}
              aria-label={`Rename ${v.name}`}
              disabled={v.retired}
              onBlur={(e) => {
                const next = e.target.value.trim();
                if (next !== "" && next !== v.name) void vehicleAct(() => putVehicle(v.id, { name: next }));
              }}
            />
            {v.needsReview && !v.retired && (
              <>
                <span className="state unconfirmed">needs review</span>
                <button type="button" className="btn ghost" onClick={() => void vehicleAct(() => putVehicle(v.id, { needsReview: false }))}>
                  Approve
                </button>
              </>
            )}
            {v.retired && <span className="state retired">retired</span>}
            <button
              type="button"
              className="btn ghost"
              onClick={() => void vehicleAct(() => putVehicle(v.id, { retired: !v.retired }))}
            >
              {v.retired ? "Bring back" : "Retire"}
            </button>
          </div>
        ))}
        <div className="vehicle-row">
          <input
            aria-label="New vehicle"
            type="text"
            maxLength={200}
            placeholder="Add a vehicle"
            value={newVehicle}
            onChange={(e) => setNewVehicle(e.target.value)}
          />
          <button
            type="button"
            className="btn ghost"
            disabled={newVehicle.trim() === ""}
            onClick={() =>
              void vehicleAct(async () => {
                await postVehicle(product.id, newVehicle.trim());
                setNewVehicle("");
              })
            }
          >
            Add vehicle
          </button>
        </div>
      </fieldset>
      <p className="form-error" role="alert" tabIndex={-1} ref={errorRef}>
        {error ?? ""}
      </p>
      <div className="record-actions">
        <button id="save-rename" className="btn primary" type="submit" disabled={saving}>
          Save
        </button>
        <button id="cancel-rename" className="btn ghost" type="button" onClick={onClose}>
          Done
        </button>
      </div>
    </form>
  );
}

/* The two-arm form. Initial values come from the current declaration; with
   none recorded the select is blank and the date is today (UI/UX review). */
function PhaseForm({
  product,
  current,
  onClose,
}: {
  product: Product;
  current: CurrentDeclaration | null;
  onClose: () => void;
}): JSX.Element {
  const { store } = useAppState();
  /* the form offers the reader's OWN calendar day as default and `max` — a
     day that has happened for them; the RULE judges against the UTC day, as
     the server does (PP3). West of UTC the local day is never later than the
     UTC one; east of it a local day the UTC clock has not reached is refused
     here and on the server with the same words. */
  const max = localToday();
  const [arm, setArm] = useState<"phase" | "not-in-pdp">(current?.kind === "not-in-pdp" ? "not-in-pdp" : "phase");
  const [phase, setPhase] = useState<string>(current?.kind === "phase" ? String(current.phase) : "");
  const [effective, setEffective] = useState<string>(current?.kind === "phase" ? current.effective : max);
  const [unconfirmed, setUnconfirmed] = useState<boolean>(current?.kind === "phase" ? current.unconfirmed : false);
  const [reason, setReason] = useState<string>(current?.kind === "not-in-pdp" ? current.reason : "");
  const [error, setError] = useState<string | null>(null);
  /* counts refusals, so the SAME reason twice re-focuses and re-announces */
  const [refusals, setRefusals] = useState(0);
  const [saving, setSaving] = useState(false);
  const firstRef = useRef<HTMLInputElement>(null);
  const errorRef = useRef<HTMLParagraphElement>(null);

  useEffect(() => firstRef.current?.focus(), []);
  useEffect(() => {
    if (refusals > 0) errorRef.current?.focus();
  }, [refusals]);

  const refuse = (reason: string): void => {
    setError(reason);
    setRefusals((n) => n + 1);
  };

  const input = (): PhaseDeclarationInput =>
    arm === "phase"
      ? { kind: "phase", phase: (phase === "" ? NaN : Number(phase)) as PdpPhase, effective, unconfirmed }
      : { kind: "not-in-pdp", reason };

  const save = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    if (saving) return;
    const body = input();
    /* the same rule the server applies, said sooner (AA2) */
    const sooner = declarationReason(body, utcToday(), current, product.name);
    if (sooner) return refuse(sooner);
    setSaving(true);
    const refused = await declarePhase(store, product.id, body);
    setSaving(false);
    if (refused) return refuse(refused);
    onClose();
  };

  const onKey = (e: KeyboardEvent): void => {
    if (e.key === "Escape") onClose();
  };

  return (
    <form id="phase-form" className="card-form" onSubmit={(e) => void save(e)} onKeyDown={onKey}>
      <fieldset>
        <legend>Declaration</legend>
        <label className="field phase-arm">
          <input
            type="radio"
            name="decl"
            id="decl-phase"
            ref={firstRef}
            checked={arm === "phase"}
            onChange={() => setArm("phase")}
          />{" "}
          In the PDP, at phase
        </label>
        <div className="phase-arm-fields">
          <label>
            Phase{" "}
            <select id="phase-select" value={phase} disabled={arm !== "phase"} onChange={(e) => setPhase(e.target.value)}>
              <option value="">— choose —</option>
              {PDP_PHASES.map((p) => (
                <option key={p} value={String(p)}>
                  Phase {p} · {PDP_PHASE_NAMES[p]}
                </option>
              ))}
            </select>
          </label>
          <label>
            Effective{" "}
            <input
              type="date"
              id="phase-effective"
              max={max}
              value={effective}
              disabled={arm !== "phase"}
              onChange={(e) => setEffective(e.target.value)}
            />
          </label>
          <label>
            <input
              type="checkbox"
              id="phase-unconfirmed"
              checked={unconfirmed}
              disabled={arm !== "phase"}
              onChange={(e) => setUnconfirmed(e.target.checked)}
            />{" "}
            Unconfirmed — a guess, not verified yet
          </label>
        </div>
        <label className="field phase-arm">
          <input
            type="radio"
            name="decl"
            id="decl-not-in-pdp"
            checked={arm === "not-in-pdp"}
            onChange={() => setArm("not-in-pdp")}
          />{" "}
          Not in the PDP
        </label>
        <div className="phase-arm-fields">
          <label>
            Why{" "}
            <input
              id="not-in-pdp-reason"
              maxLength={300}
              placeholder="e.g. vehicle bought for a customer program"
              value={reason}
              disabled={arm !== "not-in-pdp"}
              onChange={(e) => setReason(e.target.value)}
            />
          </label>
        </div>
      </fieldset>
      <p className="form-error" role="alert" tabIndex={-1} ref={errorRef}>
        {error ?? ""}
      </p>
      <div className="record-actions">
        <button id="save-declaration" className="btn primary" type="submit" disabled={saving}>
          Save declaration
        </button>
        <button className="btn ghost" type="button" onClick={onClose}>
          Cancel
        </button>
      </div>
    </form>
  );
}
